import { useMemo } from "react";
import { Drawer, Box, Stack } from "@mui/material";
import { usePathname } from "hooks/use-pathname";
import MobileNavSection from "./MobileNavSection";
import { useTheme } from "@mui/material/styles";
import history from "utils/history";
import { Section, useAuthRouteRedirection } from "layouts/Config";
import { useAppSelector } from "hooks/use-app-select";
import { selectTheme } from "redux/preferencesSlice";
import side_nav_inkfree_logo from "assets/images/side_nav_inkfree_logo.svg";
import { isOfferActive } from "helpers/methods";
import { nav } from "theme/colors";

const MOBILE_NAV_WIDTH = 280;

const useCssVars = (): Record<string, string> => {
  const themePreference = useAppSelector(selectTheme);
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    // Mirrors SideNav's tokens so the mobile drawer and the desktop drawer
    // render the same nav rows.
    if (themePreference === "dark") {
      return {
        "--nav-bg": theme.palette.common.white,
        "--nav-color": theme.palette.text.primary,
        "--nav-divider-color": theme.palette.neutral[800],
        "--nav-border-color": nav.border,
        "--nav-logo-border": theme.palette.neutral[700],
        "--nav-item-color": nav.itemText,
        "--nav-item-hover-bg": nav.hoverBg,
        "--nav-item-active-bg": nav.activeBg,
        "--nav-item-disabled-color": theme.palette.text.disabled,
        "--nav-item-icon-color": nav.itemText,
        "--nav-item-icon-active-color": nav.activeIcon,
        "--nav-item-icon-disabled-color": theme.palette.text.disabled,
        "--nav-item-chevron-color": nav.itemText,
        "--nav-scrollbar-color": theme.palette.neutral[400],
        "--nav-item-active-color": nav.activeText,
        "--nav-section-title-color": theme.palette.text.primary,
      };
    } else {
      return {
        "--nav-bg": theme.palette.common.white,
        "--nav-color": theme.palette.text.primary,
        "--nav-divider-color": theme.palette.divider,
        "--nav-border-color": nav.border,
        "--nav-logo-border": theme.palette.neutral[100],
        "--nav-section-title-color": theme.palette.text.primary,
        "--nav-item-color": nav.itemText,
        "--nav-item-hover-bg": nav.hoverBg,
        "--nav-item-active-bg": nav.activeBg,
        "--nav-item-active-color": nav.activeText,
        "--nav-item-disabled-color": theme.palette.text.disabled,
        "--nav-item-icon-color": nav.itemText,
        "--nav-item-icon-active-color": nav.activeIcon,
        "--nav-item-icon-disabled-color": theme.palette.text.disabled,
        "--nav-item-chevron-color": nav.itemText,
        "--nav-scrollbar-color": theme.palette.neutral[900],
      };
    }
  }, [theme]);
};

interface CustomProps {
  onClose?: () => void;
  open?: boolean;
  sections?: Section[];
}

const MobileNav = (props: CustomProps) => {
  const pathname = usePathname();
  const cssVars = useCssVars();
  const authRoute = useAuthRouteRedirection();
  const themePreference = useAppSelector(selectTheme);

  return (
    <Drawer
      anchor="left"
      onClose={props.onClose}
      open={props.open}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-label": "Navigation menu",
        sx: {
          ...cssVars,
          backgroundColor: "var(--nav-bg)",
          borderRightColor: "var(--nav-border-color)",
          borderRightStyle: "solid",
          borderRightWidth: 0,
          color: "var(--nav-color)",
          width: MOBILE_NAV_WIDTH,
          top: isOfferActive() ? 48 : 0,
        },
      }}
      variant="temporary"
    >
      <Stack sx={{ height: "100%" }}>
        <Stack alignItems="center" direction="row" spacing={2} sx={{ p: 2 }}>
          <Box
            component="button"
            type="button"
            aria-label="InkFree home"
            onClick={() => history.push(authRoute)}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pt: "16px",
              cursor: "pointer",
              width: "100%",
              border: "none",
              background: "transparent",
              p: 0,
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: "2px",
                borderRadius: "8px",
              },
            }}
          >
            <img
              src={side_nav_inkfree_logo}
              style={{ width: "150px" }}
              alt=""
              aria-hidden="true"
            />
          </Box>
        </Stack>
        <Stack
          component="nav"
          aria-label="Main"
          spacing={2}
          sx={{
            flexGrow: 1,
            px: 2,
            pt: 2,
          }}
        >
          {(props.sections ?? []).map((section, index) => (
            <MobileNavSection
              items={section.items}
              key={index}
              pathname={pathname}
              subheader={section.subheader}
            />
          ))}
        </Stack>
        {/* <Box sx={{ p: 3 }}>
          <Typography color="neutral.400" variant="subtitle1">
            Need help?
          </Typography>
          <Typography color="neutral.400" sx={{ mb: 2 }} variant="body2">
            Please check our docs.
          </Typography>
          <Button
            component="a"
            fullWidth
            target="_blank"
            variant="contained"
          >
            Documentation
          </Button>
        </Box> */}
      </Stack>
    </Drawer>
  );
};

export default MobileNav;
